import React from 'react';
import { StatusFilterChips } from '../../ui/StatusFilterChips';
import { useNavigate } from 'react-router-dom';
import { Loader2, Utensils, Search, ChevronRight, Users, CheckCircle2, Lock, FileDown, RotateCcw } from 'lucide-react';
import { moduleApi } from '../../../lib/api';
import { Input } from '../../ui/Input';
import { useAuth } from '../../../context/AuthContext';
import { resolveLogoUrl } from '../../../lib/resolveLogoUrl';
import { generateTechnicalSheetPdf } from '../../../lib/menusConvivesTechnicalSheetPdfGenerator';
import { generateClientRecapPdf } from '../../../lib/menusConvivesClientRecapPdfGenerator';
import { generateTrackingPdf } from '../../../lib/menusConvivesTrackingPdfGenerator';
import { COLLECTE_STATUS_LABELS } from '../collecteStatus';

interface OverviewRow {
    event_id: string;
    label: string;
    date: string;
    status: string;
    guest_count: number;
    invited_count: number;
    responded_count: number;
    finalized_at?: string | null;
    /** Calculé par le serveur : clôturée à la main, OU dont la date est passée. */
    archived?: boolean;
}

/** Page d'accueil du module "Menus & Convives" — une carte par privatisation, dans l'ordre chronologique,
 *  même gabarit visuel que les fiches contact du CRM (CRMModule.tsx) — retour explicite d'Emeni : il voulait
 *  ce même langage de carte, pas un design réinventé. Demande explicite d'Emeni : un vrai module dédié dans
 *  la barre latérale plutôt qu'un bouton caché dans la fiche événement d'Événementiel (qui reste intacte, ce
 *  module ne fait que consommer ce qu'elle produit), avec recherche et filtre par statut.
 *
 *  La CLÔTURE se fait ici et non depuis le suivi d'un événement : c'est sur cette liste qu'on voit les
 *  privatisations passées. Une fois clôturé, l'événement expose ses trois documents directement sur sa
 *  carte. Demande d'Emeni (2026-08-23). */
export function MenusConvivesListPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [rows, setRows] = React.useState<OverviewRow[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [busy, setBusy] = React.useState('');
    const [error, setError] = React.useState('');

    const reload = () => moduleApi.getEvenementielConvivesOverview()
        .then((r: any) => setRows(Array.isArray(r) ? r : [])).catch(() => { /* on garde la liste affichée */ });

    React.useEffect(() => {
        moduleApi.getEvenementielConvivesOverview().then((r: any) => setRows(Array.isArray(r) ? r : [])).catch(() => setRows([])).finally(() => setLoading(false));
    }, []);

    // Deux onglets plutôt qu'une liste unique : une privatisation passée n'a plus rien à dire au quotidien,
    // mais ses documents restent consultables. Demande d'Emeni (2026-08-25). L'archivage est automatique dès
    // le lendemain de l'événement, et n'envoie aucun email à personne.
    // 'all' vient de la pastille « Tous » ajoutée d'office par le composant partagé : sans ce cas,
    // cliquer dessus aurait affiché « En cours », ce qui n'aurait eu aucun sens.
    const [tab, setTab] = React.useState<'all' | 'encours' | 'archives'>('encours');
    const estArchivee = (r: OverviewRow) => !!(r.archived ?? r.finalized_at);
    const enCoursCount = rows.filter((r) => !estArchivee(r)).length;
    const archiveesCount = rows.length - enCoursCount;

    const filteredRows = rows.filter((r) => {
        if (search.trim() && !r.label.toLowerCase().includes(search.trim().toLowerCase())) return false;
        if (tab === 'all') return true;
        return tab === 'archives' ? estArchivee(r) : !estArchivee(r);
    });

    // Rien n'est supprimé à la clôture : elle pose un repère qui fait apparaître les téléchargements, et
    // reste réversible. La purge des données personnelles sera activée séparément, une fois le contenu des
    // documents validé par Emeni.
    const finalize = async (r: OverviewRow) => {
        const msg = `Clôturer « ${r.label} » ?\n\nLes 3 documents deviendront téléchargeables depuis cette carte.\nAucune donnée n'est supprimée, et la clôture reste réversible.`;
        if (!window.confirm(msg)) return;
        setBusy(r.event_id); setError('');
        try { await moduleApi.finalizeEvenementielCollecte(r.event_id); await reload(); }
        catch (e: any) { setError(e?.message || 'Erreur lors de la clôture.'); }
        finally { setBusy(''); }
    };

    const reopen = async (r: OverviewRow) => {
        setBusy(r.event_id); setError('');
        try { await moduleApi.finalizeEvenementielCollecte(r.event_id, true); await reload(); }
        catch (e: any) { setError(e?.message || 'Erreur.'); }
        finally { setBusy(''); }
    };

    const common = (r: OverviewRow) => ({
        eventLabel: r.label || 'Privatisation',
        eventDate: r.date,
        issuerName: user?.company_name || undefined,
        logoUrl: resolveLogoUrl(user?.logoUrl || user?.logo_url) || null,
        brandColor: user?.theme_color || undefined,
    });

    const dlSheet = async (r: OverviewRow) => {
        setBusy(r.event_id + ':sheet'); setError('');
        try {
            const sheet: any = await moduleApi.getEvenementielCollecteTechnicalSheet(r.event_id);
            await generateTechnicalSheetPdf({
                ...common(r),
                totalResponded: sheet?.total_responded || 0,
                dishes: Array.isArray(sheet?.dishes) ? sheet.dishes : [],
                restrictionsAllergies: Array.isArray(sheet?.restrictions_allergies) ? sheet.restrictions_allergies : [],
                includeAllergies: true,
            });
        } catch (e: any) { setError(e?.message || 'Erreur fiche cuisine.'); } finally { setBusy(''); }
    };

    const dlRecap = async (r: OverviewRow) => {
        setBusy(r.event_id + ':recap'); setError('');
        try {
            const recap: any = await moduleApi.getEvenementielCollecteClientRecap(r.event_id);
            await generateClientRecapPdf({
                ...common(r),
                guests: Array.isArray(recap?.guests)
                    ? recap.guests.map((g: any) => ({ name: g.name, tableNumber: g.table_number, dishesByCategory: g.dishes_by_category || {}, allergies: g.allergies || [], restrictions: g.restrictions || [] }))
                    : [],
            });
        } catch (e: any) { setError(e?.message || 'Erreur récap client.'); } finally { setBusy(''); }
    };

    const dlTracking = async (r: OverviewRow) => {
        setBusy(r.event_id + ':trk'); setError('');
        try {
            const t: any = await moduleApi.getEvenementielCollecteTracking(r.event_id);
            await generateTrackingPdf({
                ...common(r),
                total: t?.total || 0, invited: t?.invited || 0, responded: t?.responded || 0, checkedIn: t?.checked_in || 0,
                responsesClosedAt: t?.responses_closed_at || null, productionLockedAt: t?.production_locked_at || null,
                guests: Array.isArray(t?.guests) ? t.guests : [],
            });
        } catch (e: any) { setError(e?.message || 'Erreur journal de collecte.'); } finally { setBusy(''); }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-xl font-bold text-content tracking-tight flex items-center gap-2"><Utensils size={20} /> Menus &amp; Convives</h1>
                    <p className="text-content-muted text-sm mt-0.5">Menu, convives et invitations, événement par événement.</p>
                </div>
                <div className="relative w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" size={15} />
                    <Input type="text" placeholder="Rechercher une privatisation..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
                </div>
            </header>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {/* Mêmes pastilles que les suivis Extras et Commandes, réutilisées sans les modifier. */}
            {rows.length > 0 && (
                <StatusFilterChips
                    options={[
                        { value: 'encours', label: 'En cours', count: enCoursCount },
                        { value: 'archives', label: 'Archivées', count: archiveesCount },
                    ]}
                    value={tab}
                    onChange={(v) => setTab(v === 'archives' || v === 'all' ? v : 'encours')} />
            )}

            {rows.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <Utensils size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucune privatisation pour l'instant — crée-en une depuis le module Événementiel.</p>
                </div>
            ) : filteredRows.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <Search size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucune privatisation ne correspond.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredRows.map((r) => (
                        <div key={r.event_id}
                            className={`bg-white dark:bg-surface p-6 rounded-2xl border transition-all text-left group ${r.finalized_at ? 'border-brand-500/40' : 'border-border hover:border-gray-300 dark:hover:border-white/20'}`}>
                            {/* Zone cliquable séparée des actions : la carte était un <button>, on ne pouvait
                                pas y imbriquer les boutons de clôture et de téléchargement. */}
                            <div onClick={() => navigate(`/menus-convives/${r.event_id}`)} className="cursor-pointer">
                                <div className="flex items-center gap-4 mb-5">
                                    <div className="w-12 h-12 bg-surface-soft rounded-xl flex items-center justify-center border border-border group-hover:bg-surface dark:group-hover:bg-white group-hover:text-content dark:group-hover:text-slate-900 transition-all shrink-0">
                                        <Utensils size={20} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-slate-900 dark:text-content truncate">{r.label || 'Sans nom'}</h3>
                                        <p className="text-xs text-slate-500 dark:text-content-muted">{new Date(r.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                    </div>
                                    <ChevronRight className="text-slate-400 dark:text-gray-700 group-hover:text-slate-900 dark:group-hover:text-slate-900 transition-all shrink-0" size={20} />
                                </div>
                                <div className="space-y-3">
                                    <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                        <Users size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                        <span>{r.guest_count} convive{r.guest_count > 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-content-muted">
                                        <CheckCircle2 size={14} className="text-slate-500 dark:text-gray-600 shrink-0" />
                                        <span>{r.responded_count} réponse{r.responded_count > 1 ? 's' : ''} reçue{r.responded_count > 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                                <div className="mt-5 pt-5 border-t border-border flex items-center justify-between">
                                    <span className="text-[10px] text-slate-500 dark:text-gray-600 font-bold uppercase tracking-widest">Statut</span>
                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${r.finalized_at ? 'bg-brand-600 text-brand-fg border-brand-600' : 'bg-brand-500/10 text-brand-600 dark:text-brand-400 border-brand-500/20'}`}>
                                        {r.finalized_at ? 'Clôturé' : (COLLECTE_STATUS_LABELS[r.status] || r.status)}
                                    </span>
                                </div>
                            </div>

                            {/* Clôture et documents, sur la carte de l'événement : c'est ici qu'on voit ceux qui
                                sont passés. Les trois téléchargements n'apparaissent qu'une fois clôturé. */}
                            <div className="mt-4 pt-4 border-t border-border space-y-2">
                                {!r.finalized_at ? (
                                    <button type="button" onClick={() => finalize(r)} disabled={busy === r.event_id}
                                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-brand-600 text-brand-600 dark:text-brand-400 hover:bg-brand-500/10 text-xs font-black uppercase tracking-wide disabled:opacity-50">
                                        {busy === r.event_id ? <Loader2 size={13} className="animate-spin" /> : <Lock size={13} />} Clôturer
                                    </button>
                                ) : (
                                    <>
                                        <div className="grid gap-1.5">
                                            <button type="button" onClick={() => dlSheet(r)} disabled={busy === r.event_id + ':sheet'}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content hover:bg-surface disabled:opacity-50">
                                                {busy === r.event_id + ':sheet' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Fiche technique cuisine
                                            </button>
                                            <button type="button" onClick={() => dlRecap(r)} disabled={busy === r.event_id + ':recap'}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content hover:bg-surface disabled:opacity-50">
                                                {busy === r.event_id + ':recap' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Récap convives (client)
                                            </button>
                                            <button type="button" onClick={() => dlTracking(r)} disabled={busy === r.event_id + ':trk'}
                                                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-surface-soft border border-border text-xs font-bold text-content hover:bg-surface disabled:opacity-50">
                                                {busy === r.event_id + ':trk' ? <Loader2 size={13} className="animate-spin" /> : <FileDown size={13} />} Journal de collecte
                                            </button>
                                        </div>
                                        <button type="button" onClick={() => reopen(r)} disabled={busy === r.event_id}
                                            className="w-full flex items-center justify-center gap-1 text-[10px] text-content-muted hover:text-content underline">
                                            <RotateCcw size={10} /> Rouvrir
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
